import {Queries} from '../../api/queries';
import {useSetState} from '../../hooks';
import TagPicker from './TagPicker';
import {Box, Button, Checkbox, Icon, Modal, Text} from '@sproutsocial/racine';
import {useQuery} from '@tanstack/react-query';
import {type SyntheticEvent, useCallback, useMemo} from 'react';

interface TagPickerModalProps {
	selectedTagIds: Set<string>;
	excludedTagIds?: Set<string>;
	allowExclusions?: boolean;
	onSave(selectedTagIds: Set<string>, excludedTagIds?: Set<string>): void;
	onCancel(): void;
}

const TagPickerModal = (props: TagPickerModalProps) => {
	const {onSave, onCancel} = props;

	const {data: tags} = useQuery(Queries.tags());

	const selectedTagIds = useSetState(props.selectedTagIds);
	const excludedTagIds = useSetState(props.excludedTagIds);

	const onTagIncluded = useCallback(
		(tagId: string) => {
			selectedTagIds.add(tagId);
		},
		[selectedTagIds],
	);

	const onTagUnincluded = useCallback(
		(tagId: string) => {
			selectedTagIds.delete(tagId);
		},
		[selectedTagIds],
	);

	const onTagExcluded = useCallback(
		(tagId: string) => {
			excludedTagIds.add(tagId);
		},
		[excludedTagIds],
	);

	const onTagUnexcluded = useCallback(
		(tagId: string) => {
			excludedTagIds.delete(tagId);
		},
		[excludedTagIds],
	);

	const allTagsExcluded = useMemo<boolean>(() => {
		return tags?.every((tag) => excludedTagIds.has(tag.id)) ?? false;
	}, [tags, excludedTagIds]);

	const onExcludeAll = useCallback(
		(event: SyntheticEvent<HTMLInputElement>) => {
			if (allTagsExcluded) {
				excludedTagIds.clear();
			} else {
				tags?.forEach((tag) => {
					if (!excludedTagIds.has(tag.id)) {
						excludedTagIds.add(tag.id);
					}
				});
			}
		},
		[allTagsExcluded, tags, excludedTagIds],
	);

	const handleSave = useCallback(() => {
		onSave(new Set(selectedTagIds), new Set(excludedTagIds));
	}, [onSave, selectedTagIds, excludedTagIds]);

	return (
		<Modal isOpen>
			<Modal.Header title='Filter Tags' />

			<Modal.Content>
				<Text as='div' fontWeight='semibold' mb='space.300'>
					Must Have
				</Text>
				<Box display='flex' flexDirection='column'>
					<TagPicker
						selectedTagIds={selectedTagIds}
						onTagDeselected={onTagUnincluded}
						onTagSelected={onTagIncluded}
					/>
				</Box>

				{props.allowExclusions && (
					<>
						<Box borderBottom='500' borderColor='container.border.base' my='space.400' />

						<Text as='div' fontWeight='semibold' mb='space.300'>
							Can't Have
						</Text>
						<Box display='flex' flexDirection='column'>
							<Box display='flex' alignItems='center' mb='space.200'>
								<Checkbox
									id='exclude-all-tags'
									checked={excludedTagIds.size > 0}
									indeterminate={!allTagsExcluded && excludedTagIds.size > 0}
									onChange={onExcludeAll}
									mr='space.300'
								/>
								<Icon name='tag' color='neutral.1000' mr='space.300' />
								Exclude All
							</Box>

							<TagPicker
								selectedTagIds={excludedTagIds}
								onTagDeselected={onTagUnexcluded}
								onTagSelected={onTagExcluded}
							/>
						</Box>
					</>
				)}
			</Modal.Content>

			<Modal.Footer>
				<Box display='flex' alignItems='center' justifyContent='flex-end'>
					<Button mr='space.300' onClick={onCancel}>
						Cancel
					</Button>
					<Button appearance='primary' onClick={handleSave} width={100}>
						Save
					</Button>
				</Box>
			</Modal.Footer>
		</Modal>
	);
};

export default TagPickerModal;
